import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function ContemporaryFinance52() {
  const contentRef = useRef(null);
  const titleText = "The Origins and Modern Architecture of Fiat Money";
  const links = {
    prev: "/contemporaryfinance-51",
    index: "/index-finance",
    next: "/contemporaryfinance-53",
  };
  const questions = [
    {
      text: "1. Why did many governments abandon the gold standard during the First World War??",
      options: [
        {
          value: "A",
          label: "A. To stabilize international trade.",
        },
        {
          value: "B",
          label: "B.  To finance large-scale war expenditures.",
        },
        {
          value: "C",
          label: "C. To reduce inflationary pressures.",
        },
        {
          value: "D",
          label: "D. To encourage private banking independence.",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What is the primary characteristic that distinguishes fiat money from commodity money?",
      options: [
        {
          value: "A",
          label: "A. Fiat money is backed by gold or silver reserves",
        },
        {
          value: "B",
          label: "B. Fiat money derives value from government authority",
        },
        {
          value: "C",
          label: "C. Fiat money cannot be issued by central banks",
        },
        {
          value: "D",
          label: "D. Fiat money is always pegged to another currency",
        },
      ],
      correct: "B",
    },
    {
      text: "3. What is one major consequence of giving central banks like the Federal Reserve control over fiat money issuance?",
      options: [
        { value: "A", label: "A. It guarantees long-term price stability" },
        {
          value: "B",
          label: "B. It removes political influence from money creation",
        },
        {
          value: "C",
          label:
            "C. It allows expansion of money supply through debt and credit",
        },
        { value: "D", label: "D. It prevents financial crises from occurring" },
      ],
      correct: "D",
    },
    {
      text: "4. Why is fiat money unstable in the long run?",
      options: [
        {
          value: "A",
          label: "A. It is tied too closely to the supply of gold and silver",
        },
        {
          value: "B",
          label: "B.  Its supply is fixed",
        },
        {
          value: "C",
          label: "C.  Its value depends solely on government decree",
        },
        {
          value: "D",
          label: "D. It restricts governments from creating new credit",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={52}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 52</b>! Today we trace the journey of fiat money from
        its turbulent beginnings during the First World War to its intricate
        modern structure. The pressures of war forced governments and central
        banks to experiment with money creation on a scale never seen before,
        breaking the long-standing link between currency and gold. What began as
        an emergency measure revealed both the possibilities and the
        vulnerabilities of a system no longer anchored to precious metals. From
        there, the architecture of fiat money continued to evolve, with
        institutions like the Federal Reserve at its core, overseeing the
        circulation of credit, the management of reserves, and the settlement of
        vast financial flows. The system expanded far beyond national borders,
        and the U.S. dollar became the backbone of global commerce, tying
        economies together through an interdependent web of obligations. By
        examining these developments, we can see how a financial structure built
        on trust, credit, and state authority came to define modern money.
        <br />
        <br />
        <b>Fiat Money and the First World War</b>
        <br />
        <br />
        To understand the evolution of fiat money and the role it came to play
        in the twentieth century, it is important to step back into the backdrop
        of World War I. The origins of this conflict were rooted in both
        political rivalries and centuries of unresolved tensions between
        empires. The spark came in June 1914, when Archduke Franz Ferdinand of
        Austria, heir to the Austro-Hungarian throne, was assassinated in
        Sarajevo by a Serbian nationalist. This act was not an isolated incident
        of violence but rather part of a broader resistance against the
        influence of the Austro-Hungarian Empire over Serbia. Austria-Hungary
        interpreted the assassination as a deliberate act of rebellion and
        responded by declaring war on Serbia. What began as a regional
        confrontation quickly escalated into a global conflict due to the
        intricate web of military alliances that had formed among European
        powers.
        <br />
        Russia, determined to protect its strategic and cultural ties with
        Serbia, mobilized its forces in support of the smaller nation. Germany,
        which had a standing military alliance with Austria-Hungary, felt
        compelled to defend its partner and declared war on Russia. France,
        bound by a pre-existing alliance with Russia, was then drawn into the
        conflict on the side opposing Germany and Austria-Hungary. The United
        Kingdom, although initially hesitant, entered the war shortly after,
        concerned about the balance of power in Europe. Britain feared that a
        German victory would establish dominance over the continent, threatening
        both British influence and control over global trade routes. Thus,
        within weeks of the assassination in Sarajevo, nearly every major
        European power was pulled into what became known as the Great War.
        <br />
        Once war was declared, nations faced an immediate problem: financing a
        military campaign of unprecedented scale. Wars of the past had been
        expensive, but the industrial era brought new dimensions to warfare.
        Modern weapons, massive standing armies, and the mobilization of entire
        economies demanded enormous sums of money. The United Kingdom, a leading
        financial power at the time, sought to raise funds through traditional
        means. The government issued war bonds, offering interest rates higher
        than ordinary government securities, in an attempt to attract investors
        who would lend their savings to the war effort. The expectation was that
        citizens, driven by patriotism and the promise of future returns, would
        come forward to finance the war. However, the response was tepid. Many
        investors were wary of lending to a government involved in a long and
        costly war, especially when the prospects of victory were uncertain.
        <br />
        At this point, the Bank of England intervened in a decisive manner. To
        ensure that the bonds would not go unsold, the Bank created new currency
        to purchase these bonds indirectly. This practice injected large amounts
        of newly created money into the economy, money that had not been
        generated through productive activity such as trade, manufacturing, or
        natural resource extraction. Instead, it was created by the central bank
        through an act of monetary expansion. In effect, the war was financed by
        printing money, a practice that marked one of the first large-scale uses
        of fiat currency in modern history. The immediate result of this
        monetary expansion was inflation. As the supply of money grew without a
        corresponding increase in goods and services, prices across Britain
        began to rise. Households that relied on fixed wages or savings found
        that their purchasing power was steadily eroded. The currency they held
        could buy less than before, and the cost of living climbed sharply. Over
        time, the link between the British pound and gold which was historically
        the foundation of trust in the currency was weakened. While Britain had
        long promised that its currency could be redeemed in gold, the sheer
        volume of money being created during the war made such promises
        impossible to honor. Redeemability defaulted, and the idea of paper
        money as something equivalent to gold was effectively abandoned.
        <br />
        This was a turning point in monetary history. For centuries, currency
        had been tied in some form to precious metals, particularly gold and
        silver, which served as a check on how much money could be issued. The
        events of World War I demonstrated that governments, working in concert
        with central banks, could bypass this limitation by printing money at
        will when the demands of war or crisis became too great. This practice
        revealed both the potential power and the inherent risks of fiat money:
        while it allowed governments to mobilize resources quickly in
        emergencies, it also created the conditions for inflation and economic
        instability.
        <br />
        It is important to recognize that Britain was not the only nation to
        turn to money creation as a tool for financing the war. Across Europe,
        similar policies were adopted. Governments that found themselves unable
        to raise enough funds through taxation or voluntary bond sales relied on
        their central banks to create money. Even the United States, which
        initially remained neutral, eventually joined the war in 1917 and
        engaged in similar practices to support its military efforts. However,
        Britain’s role carried unique consequences. At the time, the British
        pound was considered the world’s reserve currency, widely trusted and
        used in international trade and finance. Its value was not only critical
        to the domestic British economy but also to countless other nations,
        especially developing ones, whose currencies were either pegged to the
        pound or influenced by its stability.
        <br />
        When Britain’s currency began to lose value due to inflation, the ripple
        effects spread far beyond its borders. Countries that had little or no
        involvement in the war found themselves suffering the consequences of
        Britain’s monetary policies. Their currencies, tied in one way or
        another to the pound, also lost purchasing power. This illustrates the
        interconnected nature of global finance even a century ago: when the
        reserve currency of the world was weakened, economies around the globe
        felt the impact.
        <br />
        For ordinary people, the experience of inflation during and after the
        war was deeply unsettling. Savings that had been carefully built up over
        years were eroded in value. Families who had lived comfortably before
        the war suddenly struggled with rising prices for basic necessities. The
        promises of governments and banks that paper currency was secure and
        redeemable were revealed to be fragile under the pressure of crisis.
        Trust in money, something so fundamental to daily life, was shaken in
        ways that would influence economic policy for decades to come.
        <br />
        The Great War, therefore, was more than just a military conflict. It was
        also a financial experiment on a massive scale. The reliance on fiat
        money to finance the war effort provided a real-world test of
        centralized money printing. In Britain’s case, the system did not
        collapse outright, but it did fail in one of its key promises: that
        paper currency could always be exchanged for gold. Once this link was
        broken, the foundation of the monetary system shifted. The war
        demonstrated that governments and banks had both the means and the
        willingness to abandon traditional monetary constraints when
        circumstances demanded it.
        <br />
        n the years that followed, debates about fiat money, gold standards, and
        the role of central banks would become central to economic and political
        discourse. The lessons of World War I would echo through the twentieth
        century, influencing how future wars were financed, how economic crises
        were managed, and how ordinary people understood the nature of money
        itself. For many, the inflation of this period was a warning about the
        dangers of unchecked money creation. For others, it was a demonstration
        of the flexibility and power that fiat money provided in times of
        emergency. Either way, the events of the First World War marked a
        defining chapter in the story of modern finance.
        <br />
        <br />
        <b>The Architecture of Modern Fiat Money</b>
        <br />
        <br />
        Now that we are familiar with the origins of Fiat Money let use explore
        an overview of the modern fiat money eco-system as of today. The
        financial system we live under today is a vast and layered construction.
        It is a design where money is continuously manufactured and retired,
        where some participants are rewarded by proximity to its creation, while
        others watch their purchasing power steadily diminish. Those who are
        small, isolated, or far from the levers of credit often pay the heaviest
        price, while those who are large, well connected, or institutional find
        themselves cushioned by privilege. Over decades, debt that once sat in
        private hands has migrated toward the public purse, ensuring that the
        machine never resets fully. For a time, this appears to suppress
        instability, yet the pressure always reemerges, often suddenly. To see
        how this edifice works, one must begin with its foundation: the Federal
        Reserve System.
        <br />
        <br /> <u>The Federal Reserve - A Hybrid Institution: </u> At the heart
        of American finance lies the Federal Reserve, or simply “the Fed.” It is
        neither wholly public nor fully private, but rather a hybrid entity, a
        creation of Congress in 1913 that has been reshaped many times since.
        The Fed maintains the most fundamental ledger of the American economy
        and, by extension, much of the global economy as well.
        <br />
        Its structure is peculiar. On the private side, there are twelve
        regional reserve banks, each functioning as a bank for banks in its
        territory. These institutions issue physical dollars, manage reserves,
        and connect the commercial banking system to the central authority. The
        New York Federal Reserve stands above the others, wielding
        disproportionate power because it conducts the central bank’s open
        market operations and is as large as all others combined. Commercial
        banks hold stock in these regional institutions, appoint many of their
        directors, and collect dividends on their shares.
        <br />
        The public dimension is represented by the Board of Governors in
        Washington, D.C. This panel of seven officials is appointed by the
        president and confirmed by the Senate, each serving fourteen-year terms
        that are meant to transcend political cycles. The Chair of this board is
        the most powerful figure in the institution, guiding its overall
        strategy. Once the Fed covers its costs and pays dividends to member
        banks, all additional profits are remitted to the Treasury.
        <br />
        Policy itself is set by the Federal Open Market Committee, a group of
        twelve that includes the seven governors, the president of the New York
        Fed, and four others drawn on rotation from the remaining banks. This
        ensures that government appointees always hold the majority of votes,
        even though the private sector retains a significant voice.
        <br />
        <br />
        <u>The Balance Sheets of Banks and the Fed: </u> A commercial bank
        resembles a warehouse of promises. On the asset side, its holdings
        consist mostly of loans: mortgages, business credit, personal debt,
        student lending, car loans, credit cards, and more. It may also hold
        securities such as government bonds. A small amount of cash is kept in
        vaults, but the majority of reserves exist as digital entries at the
        Federal Reserve.
        <br />
        On the liability side, banks owe money to their depositors. When you
        place dollars in a savings or checking account, the bank records this as
        its own obligation to you, while using your funds as a cheap source of
        capital. The bank earns income by charging higher rates on loans than it
        pays on deposits, supplemented by fees. To remain solvent, it must
        always maintain assets that outweigh its liabilities.
        <br />
        The Federal Reserve itself maintains a similar structure. Its assets
        include U.S. Treasury securities and, since the financial crisis of
        2008, mortgage-backed instruments. Its liabilities are the dollars in
        circulation and the reserves that commercial banks hold with it. In
        practice, this creates a circular system. Every asset is another party’s
        liability, stacked like an unending chain.
        <br />
        This can be compared to an old parable of the world resting on the back
        of a giant turtle, which itself rests on another turtle, and so on
        without end. Bank deposits are liabilities of banks, backed by the
        liabilities of borrowers. Banks’ reserves are liabilities of the Fed,
        backed by the liabilities of the federal government in the form of
        Treasury bonds. Ultimately, the entire structure rests on the
        government’s ability to tax and enforce obligations. Unlike gold, which
        is no one’s liability and represents stored effort, today’s financial
        system is sustained by interlocking IOUs. <br />
        <br />
        <u>How Settlements Occur: Fedwire and Beyond: </u> Whenever money is
        moved between people or businesses, banks must reconcile those movements
        behind the curtain. If you buy groceries with a card, or transfer money
        to a friend, the small transactions are pooled and eventually netted out
        between banks.
        <br />
        Before the Federal Reserve, these settlements required armored transfers
        of gold and notes. With the creation of the Fed, telecommunication
        quickly replaced physical shipments. By the 1910s, banks were already
        using Morse code to instruct the central bank to shift reserves between
        accounts. That system evolved into what is now known as Fedwire, the
        main artery of American money settlement.
        <br />
        Fedwire is enormous. In 2022, it processed over a quadrillion dollars in
        transactions, with an average settlement size above five million
        dollars. Retail-level payments ultimately cascade upward into these huge
        institutional settlements. In 2023, the Federal Reserve introduced
        FedNow, a real-time payment system for bank customers. This system
        allows instant transfers between accounts while the Fed silently moves
        reserves in the background. It also gives the central bank a closer look
        at individual flows of money than before, since transactions no longer
        need to be bundled into larger aggregates.
        <br />
        Alongside Fedwire exists CHIPS, a private sector settlement network
        operated by large banks. It serves only a few dozen institutions but
        handles vast sums because those institutions dominate the market.
        Together, Fedwire and CHIPS settle most of the transactions in the
        United States.
        <br />
        <br />
        <u>The Global Dimension: </u>While each country has its own central bank
        and settlement system, nearly all are entangled with the Federal
        Reserve. The dollar’s role as the world’s reserve currency means that
        foreign central banks often hold U.S. Treasuries alongside their
        domestic assets. In addition, many governments, corporations, and banks
        borrow in dollars even if they have no direct tie to the United States.
        <br />
        Today, the scale of dollar influence is staggering. Almost 90 percent of
        foreign exchange trades involve the dollar. About 60 percent of
        international reserves are denominated in dollars, and nearly half of
        all trade and cross-border loans are invoiced in dollars. In effect, the
        Federal Reserve’s balance sheet forms the core ledger of global
        commerce.
        <br />
        This global system is also circular. Central banks across the world hold
        each other’s currencies and government bonds, which are themselves
        liabilities. Although faster than the days of ships and couriers, the
        structure remains fragile, and international transfers are still
        surprisingly slow and expensive.
        <br />
        Most cross-border payments depend on SWIFT, a messaging network founded
        in the 1970s that has changed little since. Transfers can be delayed or
        blocked, and fees for remittances often remain above six percent.
        Fintech firms have made improvements at the edges, but the core system
        still operates on decades-old infrastructure.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        The evolution of fiat money began as a temporary response to crisis,
        when governments abandoned the gold standard during the First World War
        in order to finance their war efforts. Once the direct link to gold was
        broken, money was redefined not as a commodity with intrinsic value but
        as a claim upheld by law and state authority. Over time, this
        arrangement developed into a sophisticated global system, with central
        banks particularly the Federal Reserve managing credit, reserves, and
        liquidity in ways that allowed economies to expand beyond the limits of
        precious metal supply. Fiat money thus became a foundation for modern
        banking, enabling vast networks of trade, debt, and settlement to
        operate across borders. While the system rests on confidence rather than
        tangible backing, its reach and influence have shaped nearly every
        aspect of the world’s economic order today.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In the next lesson, we move from the mechanics of money creation to its
        consequences. We will examine how cycles of credit expansion and
        contraction shape booms, busts, and financial crises, and why periods of
        apparent stability often hide growing systemic risk. By tracing how
        leverage builds, how confidence becomes self-reinforcing, and how panic
        can unwind years of credit in a matter of weeks, we will see why fiat
        systems are prone to recurring crises and why central bank
        interventions, while stabilizing in the short term, often deepen
        long-term fragility.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default ContemporaryFinance52;
